import { useMemo, useState } from 'react';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useProgramItemExercises } from '../../../hooks/useProgramItemExercises';
import {
  useDeleteProgramItems,
  useGetProgramItemById,
} from '../../../redux/api/programsAPI';
import { IProgramItem } from '../../../types/interfaces/program-item';
import { groupBy } from '../../../utils/collections/array-utils';
import { orderProgramItem } from '../../../utils/training/set-utils';

export function useViewProgramItem(programItemId: string) {
  const {
    data: baseProgramItem,
    isFetching: isFetchingBase,
    isLoading: isLoadingBase,
    isError: isBaseError,
    error: baseError,
    refetch,
  } = useGetProgramItemById(programItemId);

  const canConfigureAccess =
    baseProgramItem?.permissions?.canConfigureAccess === true;

  const { data: programItemWithShares, isFetching: isFetchingShares } =
    useGetProgramItemById(programItemId, { skip: !canConfigureAccess });

  const programItemResponse: IProgramItem | undefined = useMemo(
    () => programItemWithShares ?? baseProgramItem,
    [programItemWithShares, baseProgramItem],
  );

  const isFetching =
    isFetchingBase ||
    (canConfigureAccess && isFetchingShares && !programItemWithShares);

  const { exercises } = useProgramItemExercises({
    programItemId,
  });
  const { back } = useNavigationStack();
  const [deleteProgramItem] = useDeleteProgramItems();
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);

  const orderedProgramItem: IProgramItem = useMemo(() => {
    if (!programItemResponse || !exercises) return null;

    return orderProgramItem(programItemResponse, exercises);
  }, [programItemResponse, exercises]);

  const programSetGroups = useMemo(
    () =>
      orderedProgramItem?.programSets
        ? groupBy(orderedProgramItem.programSets, 'setNumber')
        : null,
    [orderedProgramItem],
  );

  function onProgramItemDelete() {
    deleteProgramItem({
      id: programItemId,
    })
      .unwrap()
      .then(() => {
        setShowDeleteConfirmation(null);
        back();
      })
      .catch((e) => console.log(e));
  }

  return {
    programSetGroups,
    programItem: orderedProgramItem,
    isLoading: isLoadingBase || isFetching,
    isFetching,
    isError: isBaseError,
    error: baseError,
    refetch,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    onProgramItemDelete,
  };
}
